import React, { useContext, useState } from 'react';
import { Button } from 'src/components/ui/button';
import { api } from 'src/api';
import { AppContext } from 'src/hooks/AppProvider';
import { Shield, Loader2 } from 'lucide-react';
import { Textarea } from 'src/components/ui/textarea';
import { Label } from 'src/components/ui/label';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { Input } from 'src/components/ui/input';
import { ERuleType, EAttributionField, EUserTier } from 'src/interfaces';

const HC_SHEET_URL =
  'https://docs.google.com/spreadsheets/d/1x1aiInSQw1fEF-GzAfqWD3gyEQJgf97gdDwgVYY8INc/edit?gid=1788551689#gid=1788551689';

interface Props {
  address: string | undefined;
  currentAttribution?: string;
}

export const AddressMakeHc: React.FC<Props> = ({
  address,
  currentAttribution,
}) => {
  const { user } = useContext(AppContext);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    note: '',
  });

  const handleSubmit = async () => {
    if (!address || !currentAttribution) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = {
        address,
        entity: currentAttribution,
        note: formData.note,
        user: user?.email,
        isHCCluster: true,
        field: 'entity' as EAttributionField,
        rules: ['manual'] as ERuleType[],
      };

      await api.entity.submitHCClusterAttribution(data);
      toast.success(
        <div>
          High-confidence cluster attribution has been successfully saved to{' '}
          <a
            href={HC_SHEET_URL}
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            HC Cluster Google Sheet
          </a>
          .
        </div>,
      );
      setIsOpen(false);
    } catch (error) {
      console.error(error);
      setError('Failed to submit HC cluster proposal. Please try again.');
      toast.error('Failed to submit HC cluster proposal');
    } finally {
      setLoading(false);
    }
  };

  if (user?.accountType !== EUserTier.INTERNAL) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <Shield className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Make HC Cluster Attribution</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          {error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-500">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Address</Label>
              <Input value={address} readOnly className="bg-muted" />
            </div>

            <div className="space-y-2">
              <Label>Proposal Note</Label>
              <Textarea
                placeholder="Add notes about the HC attribution..."
                value={formData.note}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, note: e.target.value }))
                }
                className="min-h-[100px]"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSubmit} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Make HC'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
